import { ReactNode, useState, useRef, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X
} from "lucide-react";

interface Column<T> {
  key: string;
  header: string;
  /** Custom cell renderer */
  render?: (item: T) => ReactNode;
  /** Enable sorting for this column */
  sortable?: boolean;
  /** Column alignment */
  align?: 'left' | 'center' | 'right';
  /** Column width class */
  width?: string;
}

interface SortState {
  column: string | null;
  direction: 'asc' | 'desc';
}

interface DataTableProps<T> {
  /** Data array */
  data: T[];
  /** Column definitions */
  columns: Column<T>[];
  /** Unique key accessor */
  keyAccessor: (item: T) => string | number;
  /** Row click handler */
  onRowClick?: (item: T) => void;
  
  // --- Search (Controlled or Uncontrolled) ---
  /** Enable search input */
  searchable?: boolean;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Controlled: Current search value (enables controlled mode) */
  searchValue?: string;
  /** Controlled: Search change handler (required if searchValue provided) */
  onSearchChange?: (value: string) => void;
  /** Enable keyboard shortcuts (/ to focus, Escape to clear) */
  searchKeyboardShortcuts?: boolean;
  /** Show clear button and keyboard hint */
  searchEnhanced?: boolean;
  
  // --- Sorting (Controlled or Uncontrolled) ---
  /** Controlled: Current sort state */
  sort?: SortState;
  /** Controlled: Sort change handler */
  onSortChange?: (sort: SortState) => void;
  
  // --- Pagination (Always Controlled) ---
  /** Pagination config */
  pagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    /** Original count before filtering (for "filtered from X" display) */
    unfilteredCount?: number;
    onPageChange: (page: number) => void;
  };
  /** Enable extended pagination (page numbers, items-per-page, jump-to-page) */
  extendedPagination?: boolean;
  /** Items per page options */
  pageSizeOptions?: number[];
  /** Handler for page size change */
  onPageSizeChange?: (pageSize: number) => void;
  
  /** Loading state */
  isLoading?: boolean;
  /** Empty state content */
  emptyState?: ReactNode;
  /** Header actions (e.g., export button) */
  headerActions?: ReactNode;
  /** Number of skeleton rows to show when loading */
  skeletonRowCount?: number;
  /** Remove border wrapper (for use inside Card) */
  noBorder?: boolean;
  /** Custom class for table container */
  tableClassName?: string;
}

export function DataTable<T>({
  data,
  columns,
  keyAccessor,
  onRowClick,
  searchable,
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  searchKeyboardShortcuts,
  searchEnhanced,
  sort,
  onSortChange,
  pagination,
  extendedPagination,
  pageSizeOptions = [10, 25, 50, 100],
  onPageSizeChange,
  isLoading,
  emptyState,
  headerActions,
  skeletonRowCount = 10,
  noBorder,
  tableClassName
}: DataTableProps<T>) {
  // Internal state for uncontrolled mode
  const [internalSearch, setInternalSearch] = useState("");
  const [internalSort, setInternalSort] = useState<SortState>({ 
    column: null, 
    direction: 'asc' 
  });
  const [jumpToPage, setJumpToPage] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Determine if controlled mode
  const isSearchControlled = searchValue !== undefined;
  const isSortControlled = sort !== undefined;

  // Use controlled or internal state
  const currentSearch = isSearchControlled ? searchValue : internalSearch;
  const currentSort = isSortControlled ? sort : internalSort;

  const handleSearchChange = (value: string) => {
    if (isSearchControlled && onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearch(value);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!searchKeyboardShortcuts) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      const active = document.activeElement;
      const isEditable = 
        active?.tagName === "INPUT" ||
        active?.tagName === "TEXTAREA" ||
        (active as HTMLElement)?.isContentEditable;
      
      // "/" to focus search (when not in an editable field)
      if (e.key === "/" && !isEditable) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      
      // Escape to clear and blur (only when search is focused)
      if (e.key === "Escape" && active === searchInputRef.current) {
        handleSearchChange("");
        searchInputRef.current?.blur();
      }
    };
    
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [searchKeyboardShortcuts, isSearchControlled, onSearchChange]);

  const handleSort = (columnKey: string) => {
    const newSort: SortState = {
      column: columnKey,
      direction: currentSort.column === columnKey && currentSort.direction === 'asc' 
        ? 'desc' 
        : 'asc'
    };
    
    if (isSortControlled && onSortChange) {
      onSortChange(newSort);
    } else {
      setInternalSort(newSort);
    }
  };

  const handleJumpToPage = () => {
    if (!pagination) return;
    const page = parseInt(jumpToPage);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      pagination.onPageChange(page);
      setJumpToPage("");
    }
  };

  // Generate page numbers for extended pagination
  const getPageNumbers = (): (number | string)[] => {
    if (!pagination) return [];
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (pagination.page > 3) {
        pages.push("...");
      }
      
      const start = Math.max(2, pagination.page - 1);
      const end = Math.min(totalPages - 1, pagination.page + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (pagination.page < totalPages - 2) {
        pages.push("...");
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  const totalPages = pagination 
    ? Math.ceil(pagination.totalCount / pagination.pageSize) 
    : 1;
  const startIndex = pagination 
    ? (pagination.page - 1) * pagination.pageSize + 1 
    : 1;
  const endIndex = pagination 
    ? Math.min(pagination.page * pagination.pageSize, pagination.totalCount) 
    : data.length;

  return (
    <div className="space-y-4">
      {/* Header with search and actions */}
      {(searchable || headerActions) && (
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          {searchable && (
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder={searchPlaceholder}
                value={currentSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                className={searchEnhanced ? "pl-9 pr-16" : "pl-9"}
                aria-keyshortcuts={searchKeyboardShortcuts ? "/" : undefined}
              />
              {searchEnhanced && (
                currentSearch ? (
                  <button
                    onClick={() => handleSearchChange("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : (
                  <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded border border-border">
                    /
                  </kbd>
                )
              )}
            </div>
          )}
          {headerActions && (
            <div className="flex items-center gap-2">
              {headerActions}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className={tableClassName || (noBorder ? "overflow-hidden" : "rounded-lg border border-border overflow-hidden")}>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={`font-semibold ${column.width || ''} ${
                    column.align === 'right' ? 'text-right' : 
                    column.align === 'center' ? 'text-center' : ''
                  }`}
                >
                  {column.sortable ? (
                    <button
                      onClick={() => handleSort(column.key)}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      {column.header}
                      {currentSort.column === column.key ? (
                        currentSort.direction === 'asc' ? (
                          <ArrowUp className="h-4 w-4" />
                        ) : (
                          <ArrowDown className="h-4 w-4" />
                        )
                      ) : (
                        <ArrowUpDown className="h-4 w-4 opacity-50" />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Loading skeletons
              Array.from({ length: skeletonRowCount }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((column) => (
                    <TableCell key={column.key}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length === 0 ? (
              // Empty state
              <TableRow>
                <TableCell colSpan={columns.length} className="h-32">
                  {emptyState || (
                    <div className="text-center text-muted-foreground">
                      No data available
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              // Data rows
              data.map((item) => (
                <TableRow
                  key={keyAccessor(item)}
                  onClick={() => onRowClick?.(item)}
                  className={onRowClick ? 'cursor-pointer hover:bg-muted/30 transition-colors' : ''}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      className={
                        column.align === 'right' ? 'text-right' : 
                        column.align === 'center' ? 'text-center' : ''
                      }
                    >
                      {column.render 
                        ? column.render(item) 
                        : (item as Record<string, unknown>)[column.key] as ReactNode
                      }
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalCount > 0 && (
        extendedPagination ? (
          // Extended pagination with items-per-page, page numbers, jump-to-page
          <div className="space-y-4">
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Items per page selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Show</span>
                  <Select 
                    value={pagination.pageSize.toString()} 
                    onValueChange={(value) => onPageSizeChange?.(Number(value))}
                  >
                    <SelectTrigger className="w-[80px] h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {pageSizeOptions.map((size) => (
                        <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">per page</span>
                </div>
                
                {/* Page navigation */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => pagination.onPageChange(1)}
                    disabled={pagination.page === 1}
                    aria-label="First page"
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => pagination.onPageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  {/* Page numbers */}
                  {getPageNumbers().map((page, index) => (
                    typeof page === "number" ? (
                      <Button
                        key={index}
                        variant={pagination.page === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => pagination.onPageChange(page)}
                        aria-label={`Page ${page}`}
                        aria-current={pagination.page === page ? "page" : undefined}
                      >
                        {page}
                      </Button>
                    ) : (
                      <span key={index} className="px-2 text-muted-foreground" aria-hidden="true">...</span>
                    )
                  ))}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => pagination.onPageChange(pagination.page + 1)}
                    disabled={pagination.page === totalPages}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => pagination.onPageChange(totalPages)}
                    disabled={pagination.page === totalPages}
                    aria-label="Last page"
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Jump to page */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Go to</span>
                  <Input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={jumpToPage}
                    onChange={(e) => setJumpToPage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleJumpToPage()}
                    placeholder={pagination.page.toString()}
                    className="w-[70px] h-9 text-center"
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleJumpToPage} 
                    disabled={!jumpToPage}
                    aria-label="Go to page"
                  >
                    Go
                  </Button>
                </div>
              </div>
            )}
            
            {/* Results summary */}
            <div className="text-sm text-muted-foreground text-center">
              Showing {pagination.totalCount === 0 ? 0 : startIndex} to {endIndex} of {pagination.totalCount}
              {typeof pagination.unfilteredCount === "number" && pagination.unfilteredCount > pagination.totalCount && (
                <span className="text-muted-foreground/70"> (filtered from {pagination.unfilteredCount})</span>
              )}
            </div>
          </div>
        ) : (
          // Basic pagination
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Showing {pagination.totalCount === 0 ? 0 : startIndex} to {endIndex} of {pagination.totalCount}
              {typeof pagination.unfilteredCount === "number" && pagination.unfilteredCount > pagination.totalCount && (
                <span className="text-muted-foreground/70"> (filtered from {pagination.unfilteredCount})</span>
              )}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                onClick={() => pagination.onPageChange(1)}
                disabled={pagination.page === 1}
                aria-label="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => pagination.onPageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1 px-3">
                <span className="text-sm font-medium">{pagination.page}</span>
                <span className="text-sm text-muted-foreground">of {totalPages}</span>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => pagination.onPageChange(pagination.page + 1)}
                disabled={pagination.page === totalPages}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => pagination.onPageChange(totalPages)}
                disabled={pagination.page === totalPages}
                aria-label="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )
      )}
    </div>
  );
}
